import { useEffect, useContext, useRef, useCallback } from '@wordpress/element';
import { getExistingCheckout } from '../../util';
import { RetailerIdContext } from '../../App';
import { useCartData, useRetailerData } from '../../hooks/dutchie-queries';
import { useUpdateCheckout } from '../../hooks/dutchie-mutations';
import { useSharedOrderType } from '../../hooks/useSharedOrderType';

let instanceCounter = 0;

const OrderType = ({ forcedOrderType }) => {
    const instanceId = useRef(instanceCounter++);
    const retailerId = useContext(RetailerIdContext);
    const checkout = getExistingCheckout();
    const initialOrderTypeSet = useRef(false);
    const [globalChecked, setGlobalChecked] = useSharedOrderType();

    const { data: cartData, isFetched: isFetchedCartData } =
        useCartData(retailerId);
    const { data: retailerData, isFetched: isFetchedRetailerData } =
        useRetailerData(retailerId);

    const handleRadioChange = useUpdateCheckout(retailerId);

    const hasPickupOption = (retailerData) => {
        return (
            retailerData?.fulfillmentOptions.pickup ||
            retailerData?.fulfillmentOptions.curbsidePickup ||
            retailerData?.fulfillmentOptions.driveThruPickup
        );
    };

    const updateOrderType = useCallback((newValue) => {
        setGlobalChecked(newValue);
        handleRadioChange.mutate({
            retailerId: checkout.retailerId,
            checkoutId: checkout.checkoutId,
            orderType: newValue,
            pricingType: cartData?.pricingType,
            address: {
                city: '',
                state: '',
                street1: '',
                street2: '',
                zip: '',
            },
        });
    }, [checkout.retailerId, checkout.checkoutId, cartData?.pricingType, handleRadioChange, setGlobalChecked]);

    useEffect(() => {
        if (!isFetchedCartData || !isFetchedRetailerData || !cartData || !retailerData) {
            return;
        }

        const pickupAvailable = hasPickupOption(retailerData);
        const deliveryAvailable = retailerData?.fulfillmentOptions.delivery;
        
        if (forcedOrderType && forcedOrderType.orderType && initialOrderTypeSet.current === false && deliveryAvailable) {
            initialOrderTypeSet.current = true;
            updateOrderType(forcedOrderType.orderType.toUpperCase());
        } else if (!globalChecked && initialOrderTypeSet.current === false) {
            if (cartData.orderType) {
                if (cartData.orderType === 'PICKUP' && !pickupAvailable) {
                    updateOrderType('DELIVERY');
                } else {
                    updateOrderType(cartData.orderType);
                }
            } else if (pickupAvailable) {
                updateOrderType('PICKUP');
            } else if (deliveryAvailable) {
                updateOrderType('DELIVERY');
            }
        }
    }, [cartData, retailerData, isFetchedCartData, isFetchedRetailerData, forcedOrderType, globalChecked, updateOrderType]);

    useEffect(() => {
        const pickupLabel = document.querySelector(`#order-pickup-${instanceId.current} + label`);
        const deliveryLabel = document.querySelector(`#order-delivery-${instanceId.current} + label`);

        if (pickupLabel) {
            pickupLabel.classList.toggle('checked', globalChecked === 'PICKUP');
        }
        if (deliveryLabel) {
            deliveryLabel.classList.toggle('checked', globalChecked === 'DELIVERY');
        }
    }, [globalChecked]);

    const handleOptionChange = (newValue) => {
        updateOrderType(newValue);
    };

    return (
        <div 
            className={`headless-cart-ordertype ordertype-${globalChecked}`}
            role='radiogroup'
            aria-label='Order fulfillment method'
        >
            <div className='sr-only' id={`ordertype-description-${instanceId.current}`}>
                Select how you would like to receive your order
            </div>

            {hasPickupOption(retailerData) && (
                <div className='ordertype-option'>
                    <input
                        type='radio'
                        value='PICKUP'
                        name={`orderType-${instanceId.current}`}
                        id={`order-pickup-${instanceId.current}`}
                        checked={globalChecked === 'PICKUP'}
                        onChange={() => handleOptionChange('PICKUP')}
                        aria-describedby={`ordertype-description-${instanceId.current}`}
                    />
                    <label
                        htmlFor={`order-pickup-${instanceId.current}`}
                        className={globalChecked === 'PICKUP' ? 'checked' : ''}
                    >
                        <svg
                            xmlns='http://www.w3.org/2000/svg'
                            viewBox='0 0 448 512'
                            aria-hidden='true'
                            focusable='false'
                            className='ordertype-icon'
                        >
                            <path d='M160 112v48H288V112c0-35.3-28.7-64-64-64s-64 28.7-64 64zm-48 96H48V464H400V208H336v56 24H288V264 208H160v56 24H112V264 208zm0-48V112C112 50.1 162.1 0 224 0s112 50.1 112 112v48h64 48v48V464v48H400 48 0V464 208 160H48h64z' />
                        </svg>
                        <span>Pickup</span>
                    </label>
                </div>
            )}

            {retailerData?.fulfillmentOptions.delivery && (
                <div className='ordertype-option'>
                    <input
                        type='radio'
                        value='DELIVERY'
                        name={`orderType-${instanceId.current}`}
                        id={`order-delivery-${instanceId.current}`}
                        checked={globalChecked === 'DELIVERY'}
                        onChange={() => handleOptionChange('DELIVERY')}
                        aria-describedby={`ordertype-description-${instanceId.current}`}
                    />
                    <label
                        htmlFor={`order-delivery-${instanceId.current}`}
                        className={globalChecked === 'DELIVERY' ? 'checked' : ''}
                    >
                        <svg
                            xmlns='http://www.w3.org/2000/svg'
                            viewBox='0 0 512 512'
                            aria-hidden='true'
                            focusable='false'
                            className='ordertype-icon'
                        >
                            <path d='M127.7 106.8L103.4 176H408.6l-24.2-69.2c-5.6-16-20.8-26.8-37.8-26.8H165.4c-17 0-32.1 10.7-37.8 26.8zm-79.6 82L82.3 90.9C94.7 55.6 128 32 165.4 32H346.6c37.4 0 70.7 23.6 83.1 58.9l34.3 97.9C492.6 205.4 512 236.4 512 272v80 48 56c0 13.3-10.7 24-24 24s-24-10.7-24-24V400H48v56c0 13.3-10.7 24-24 24s-24-10.7-24-24V400 352 272c0-35.6 19.3-66.6 48.1-83.2zM416 224H96c-26.5 0-48 21.5-48 48v80H464V272c0-26.5-21.5-48-48-48zM112 256a32 32 0 1 1 0 64 32 32 0 1 1 0-64zm256 32a32 32 0 1 1 64 0 32 32 0 1 1 -64 0z' />
                        </svg>
                        <span>Delivery</span>
                    </label>
                </div>
            )}
        </div>
    );
};

export default OrderType;
